import type {
  Invitation,
  Location,
  Match,
  Notification,
  Organizer,
  PublicUser,
  Team,
  Tournament,
  User,
} from "@/types";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  updateDoc,
  where,
  WhereFilterOp,
} from "firebase/firestore";

import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";

// ============================================================================
// Types
// ============================================================================

export interface BaseEntity {
  id: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export type QueryFilter = {
  field: string;
  operator: WhereFilterOp;
  value: any;
};

export type QueryOrder = {
  field: string;
  direction?: "asc" | "desc";
};

export type QueryOptions = {
  filters?: QueryFilter[];
  orderBy?: QueryOrder[];
  limit?: number;
};

// ============================================================================
// Firestore Adapter
// ============================================================================

export class FirestoreAdapter<T extends BaseEntity> {
  private collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  // ==========================================================================
  // READ Operations
  // ==========================================================================

  /**
   * Get a single document by ID
   */
  async getById(id: string): Promise<T | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return formatFirestoreDocument(docSnap) as T;
    } catch (error) {
      console.error(
        `Error getting document from ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Get all documents matching the query options
   */
  async getAll(options?: QueryOptions): Promise<T[]> {
    try {
      const constraints: QueryConstraint[] = [];

      // Add filters
      if (options?.filters) {
        options.filters.forEach((filter) => {
          constraints.push(where(filter.field, filter.operator, filter.value));
        });
      }

      // Add ordering
      if (options?.orderBy) {
        options.orderBy.forEach((order) => {
          constraints.push(orderBy(order.field, order.direction || "asc"));
        });
      }

      // Add limit
      if (options?.limit) {
        constraints.push(limit(options.limit));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => formatFirestoreDocument(doc) as T);
    } catch (error: any) {
      // Handle NOT_FOUND error gracefully (empty database/collection doesn't exist yet)
      if (
        error?.code === "not-found" ||
        error?.message?.includes("NOT_FOUND")
      ) {
        return [];
      }

      console.error(
        `Error getting documents from ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Get document by a specific field value
   */
  async getByField(field: string, value: any): Promise<T | null> {
    try {
      const results = await this.getAll({
        filters: [{ field, operator: "==", value }],
        limit: 1,
      });

      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error(
        `Error getting document by ${field} from ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  // ==========================================================================
  // CREATE Operations
  // ==========================================================================

  /**
   * Create a new document
   */
  async create(data: Omit<T, "id" | "createdAt" | "updatedAt">): Promise<T> {
    try {
      const now = new Date();
      const docData = {
        ...data,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Document was not created");
      }

      return formatFirestoreDocument(docSnap) as T;
    } catch (error) {
      console.error(
        `Error creating document in ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  // ==========================================================================
  // UPDATE Operations
  // ==========================================================================

  /**
   * Update an existing document
   */
  async update(
    id: string,
    data: Partial<Omit<T, "id" | "createdAt" | "updatedAt">>,
  ): Promise<T> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);

      if (!updatedDoc.exists()) {
        throw new Error("Document does not exist");
      }

      return formatFirestoreDocument(updatedDoc) as T;
    } catch (error) {
      console.error(
        `Error updating document in ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  // ==========================================================================
  // DELETE Operations
  // ==========================================================================

  /**
   * Delete a document
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);

      await deleteDoc(docRef);
    } catch (error) {
      console.error(
        `Error deleting document from ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  // ==========================================================================
  // UTILITY Methods
  // ==========================================================================

  /**
   * Check if a document exists
   */
  async exists(id: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      return docSnap.exists();
    } catch (error) {
      console.error(
        `Error checking document existence in ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Count documents matching query options
   */
  async count(options?: QueryOptions): Promise<number> {
    try {
      const results = await this.getAll(options);

      return results.length;
    } catch (error) {
      console.error(
        `Error counting documents in ${this.collectionName}:`,
        error,
      );
      throw error;
    }
  }
}

// ============================================================================
// Collection Adapters
// ============================================================================
// Imports
// ============================================================================

// Create singleton instances for each collection
export const usersAdapter = new FirestoreAdapter<User>("users");
export const publicProfilesAdapter = new FirestoreAdapter<PublicUser>(
  "publicProfiles",
);

export const locationsAdapter = new FirestoreAdapter<Location>("locations");

export const organizersAdapter = new FirestoreAdapter<Organizer>("organizers");

// ============================================================================

// Tower Challenger specific adapters
export const tournamentsAdapter = new FirestoreAdapter<Tournament>(
  "tournaments",
);
export const teamsAdapter = new FirestoreAdapter<Team>("teams");
export const matchesAdapter = new FirestoreAdapter<Match>("matches");
export const notificationsAdapter = new FirestoreAdapter<Notification>(
  "notifications",
);
export const invitationsAdapter = new FirestoreAdapter<Invitation>(
  "invitations",
);
